import {
	DialogCloseError,
	DialogCloseResult,
	DialogController,
} from 'aurelia-dialog';
import {
	autoinject,
	bindable,
	BindingEngine,
	computedFrom,
	Disposable,
	newInstance,
} from 'aurelia-framework';
import { ValidationController } from 'aurelia-validation';

import debounce from 'lodash-es/debounce';
import { AuditApi } from '~/api/audit-api';
import { ClassicFormsApi } from '~/api/classic-forms-api';
import { APIParameters } from '~/api/generic-api';
import { NotesApi } from '~/api/notes-api';
import { AuditDialog, AuditDialogModel } from '~/components/audit/audit-dialog';
import { ClassicPanel } from '~/components/classic-panel/classic-panel';
import { CustomisationConfig } from '~/components/customisation/customisation-config';
import {
	SimpleDialog,
	SimpleDialogModel,
	SimpleDialogModelDefaults,
	SimpleDialogType,
} from '~/components/dialog/simple-dialog';
import {
	SearchFieldModel,
	SearchFilter,
	SearchType,
} from '~/components/field/common/search-definition';
import { FilterType } from '~/components/filter/filter-options';
import { FormType } from '~/components/form/form';
import {
	NotesDialog,
	NotesDialogModel,
	NotesDialogResult,
} from '~/components/notes/notes-dialog';
import { Notification } from '~/components/notification/notification';
import { DEBOUNCE_RECORD_SELECT_DURATION, ShortcutKey } from '~/constants';
import { FormModel } from '~/models/form-model';
import { FormParameters } from '~/models/form-parameters';
import { MemberDetailsField } from '~/models/generated/entities/MemberDetails';
import { InsertResult } from '~/models/insert-result';
import { DeleteHelper } from '~/utils/delete-helper';
import { DialogUtils } from '~/utils/dialog-utils';
import { FetchUtils, HttpMethod } from '~/utils/fetch-utils';
import FormModelUtils from '~/utils/form-model-utils';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Logger } from '~/utils/logger';
import { ObjectUtils } from '~/utils/utils';

export interface ClassicPanelDialogModel<
	FormModelFieldNames extends string = string,
> extends SimpleDialogModel {
	areaName?: string;
	customApi?: string;
	customHttpMethod?: HttpMethod;
	customisationConfig?: CustomisationConfig | false;
	formExtension?: string;
	formType?: FormType;
	functionName?: string;
	onFormLoad?: (formModel: FormModel<FormModelFieldNames>) => void;
	extraReadOnlyParameters?: string[];
	preSelectedParameters?: FormParameters[];
	queryParams?: Map<string, string>;
	recordId?: number;
	hasNotesButton?: boolean;
	hasDeleteButton?: boolean;
	hasAuditButton?: boolean;
	useViewEditBehaviour?: boolean;
	onClose?: (value: DialogCloseResult) => void;
	nonCustomisableAreaHtml?: string;
}

export const ClassicPanelDialogModelDefaults: ClassicPanelDialogModel = {
	...SimpleDialogModelDefaults,
	scrollable: true,
};

@autoinject
export class ClassicPanelDialog<
	DialogModel extends ClassicPanelDialogModel<FormModelFieldNames>,
	FormModelFieldNames extends string = string,
> extends SimpleDialog<DialogModel> {
	protected classicPanel: ClassicPanel;

	private subscription: Disposable;

	protected preLoadedFormModel: FormModel<FormModelFieldNames>;

	private isSaving: boolean = false;

	@bindable private recordHasNotes: boolean = false;

	constructor(
		protected readonly dialogUtils?: DialogUtils,
		protected readonly notesApi?: NotesApi,
		protected readonly auditApi?: AuditApi,
		protected classicFormsApi?: ClassicFormsApi,
		@newInstance()
		protected readonly validationController?: ValidationController,
		protected readonly bindingEngine?: BindingEngine,
		protected readonly controller?: DialogController,
		protected readonly element?: Element,
		...rest
	) {
		super(...rest);
	}

	public activate(
		model: DialogModel,
		defaults: DialogModel = ClassicPanelDialogModelDefaults as DialogModel,
	): Promise<DialogModel> {
		let classicPanelModel = model;
		if (
			classicPanelModel.headingText === null ||
			classicPanelModel.headingText === undefined
		) {
			// NOTE(Lucas): 06/03/2020 - WEBUI-3155
			// 1. SimpleDialog's activate method overrides headingText if it does not exist. So this overriding of a
			//    non-existent headingText must be done before SimpleDialog's.
			// 2. The shallow copy of the model is done so that a changed model is not given back to the caller.
			classicPanelModel = { ...model };
			if (classicPanelModel.formType === FormType.ADD) {
				classicPanelModel.headingText = this.i18n.tr(
					'classic-panel-dialog.add-record',
				);
			} else {
				classicPanelModel.headingText = this.i18n.tr(
					'classic-panel-dialog.edit-record',
				);
			}
		}

		return super
			.activate(classicPanelModel, defaults)
			.then(() => {
				if (
					classicPanelModel.okButtonText === null ||
					classicPanelModel.okButtonText === undefined
				) {
					if (this.model.formType === FormType.ADD) {
						this.model.okButtonText = 'clickable-elements.add';
					} else {
						this.model.okButtonText = 'clickable-elements.save';
					}
				}

				// Pre-load classic form data, so that we can have the data ready when the ClassicPanel is attached.
				// We can't populate the panel here because it has not been bound yet.
				return this.preLoadClassicPanelFormModelData();
			})
			.then(() => this.model);
	}

	protected async getClassicPanelPreInsertFormModelPromise(
		functionName: string,
		params: APIParameters = {},
	): Promise<any> {
		return ClassicPanel.getPreInsertFormModelPromise(
			this.classicFormsApi,
			functionName,
			params,
		);
	}

	protected setAdditionalFilters(
		formModel: FormModel<FormModelFieldNames>,
		params: APIParameters = {},
	): FormModel<FormModelFieldNames> {
		const formParameters = this.model.preSelectedParameters;
		if (formParameters) {
			formParameters
				.filter(
					(formParam) =>
						formParam.paramType !== null &&
						formParam.paramType !== undefined,
				)
				.forEach((formParam) => {
					params[formParam.paramType] = formParam.value;
				});
		}

		if (!formParameters || formParameters.length < 1) {
			return formModel;
		}

		const searchParams = formParameters.reduce((obj, param) => {
			if (ObjectUtils.notEmpty(param.paramType)) {
				obj[param.paramType.toUpperCase() as SearchType] = param;
			}
			return obj;
		}, {});

		for (const param of formParameters) {
			let additionalFilter: SearchFilter;
			if (!ObjectUtils.notEmpty(param.paramType)) {
				continue;
			}
			if (param.paramType.toUpperCase() === SearchType.MEMBER) {
				if (!searchParams[SearchType.FUND]) {
					throw new Error(
						'Cannot set Member description when Fund is undefined.',
					);
				}
				additionalFilter = {
					operation: FilterType.EQUALS,
					not: false,
					ignoreCase: true,
					andJoin: true,
					data: MemberDetailsField.MDz_Fund,
					value: searchParams[SearchType.FUND].value,
				};

				// Set additionalFilters here so they are present on field model when we set
				// description on the `search.ts` field in getDescriptionIfPreselectValue()
				const fieldModel = formModel.fields[
					param.name
				] as SearchFieldModel;
				if (fieldModel.additionalFilters) {
					fieldModel.additionalFilters.push(additionalFilter);
				} else {
					fieldModel.additionalFilters = [additionalFilter];
				}
			}
		}

		return formModel;
	}

	/**
	 * Pre-load the classic panel FormModel or pre-insert FormModel if record data is available in the model
	 */
	protected preLoadClassicPanelFormModelData(): Promise<void> {
		let formModelPromise;

		const params: any = this.makePreInsertParams();

		if (this.model.formType === FormType.ADD && this.model.functionName) {
			formModelPromise = this.getClassicPanelPreInsertFormModelPromise(
				this.model.functionName,
				params,
			);
		} else if (this.model.functionName && this.model.recordId) {
			formModelPromise = ClassicPanel.getFormModel(
				this.classicFormsApi,
				this.model.functionName,
				this.model.recordId,
			);
		} else {
			return Promise.resolve();
		}

		return (
			formModelPromise
				.then((model) => {
					this.preLoadedFormModel = this.setAdditionalFilters(
						model,
						params,
					);
					FormModelUtils.showWarningMessagesIfPresent(model);
				})
				// Show an error and abort navigation
				.catch((error) => {
					ClassicPanel.logAndShowPreInsertError(error);
				})
		);
	}

	protected makePreInsertParams() {
		const params: any = {};
		// There can be a race condition with the binding of queryParams on the model.
		// Use preSelectedParameters first to ensure these are set on the API.
		if (this.model.preSelectedParameters) {
			this.model.preSelectedParameters.forEach((parameter) => {
				params[parameter.paramType] = parameter.value;
			});
		}
		if (this.model.queryParams) {
			this.model.queryParams.forEach((value, key) => {
				params[key] = value;
			});
		}
		if (this.model.recordId) {
			params.cloneFrom = this.model.recordId;
		}
		return params;
	}

	/**
	 * Populate the classic panel with our pre-loaded FormModel
	 */
	protected populateClassicPanelFormModel() {
		if (this.preLoadedFormModel) {
			if (this.model.formType === FormType.ADD) {
				this.classicPanel.processPreInsertFormModel(
					this.preLoadedFormModel,
				);
			} else {
				this.classicPanel.processEditFormModel(this.preLoadedFormModel);
			}
			this.preLoadedFormModel = undefined;
		}
	}

	protected formModelChanged(
		formModel: FormModel<FormModelFieldNames>,
		oldFormModel: FormModel<FormModelFieldNames>,
	): void {
		if (formModel && !oldFormModel) {
			this.onFormLoad();
		}
	}

	private onFormLoad(): void {
		if (this.model.onFormLoad) {
			this.model.onFormLoad(this.classicPanel.formModel);
		}

		// NOTE: Jake: 2019-08-28 - WEBUI-1455 / WEBUI-2290
		// "attached()" method has not finished loading the form yet, so lets focus the field
		// after the form has loaded.
		if (this.classicPanel) {
			this.classicPanel.focusFirstField();
		}
	}

	/**
	 * attachKeyboardShortcuts is overriding the default behaviour as classic-panel-dialog templates don't have
	 * controller.ok() or controller.cancel() buttons present.
	 *
	 * @override
	 */
	protected attachKeyboardShortcuts() {
		if (!this.model.useViewEditBehaviour) {
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.Save,
				this.element.querySelectorAll('[click\\.trigger="save()"]'),
			);
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.Close,
				this.element.querySelectorAll('[click\\.trigger="close()"]'),
			);
		} else {
			if (this.hasOkButton) {
				KeyboardShortcut.subscribe(
					this,
					ShortcutKey.Save,
					this.element.querySelectorAll('[click\\.trigger="save()"]'),
				);
				KeyboardShortcut.subscribe(
					this,
					ShortcutKey.Edit,
					this.element.querySelectorAll('[click\\.trigger="edit()"]'),
				);
			}
			if (this.model.hasDeleteButton) {
				KeyboardShortcut.subscribe(
					this,
					ShortcutKey.Delete,
					this.element.querySelectorAll(
						'[click\\.trigger="toggleDelete()"]',
					),
				);
			}
			if (this.model.hasNotesButton) {
				KeyboardShortcut.subscribe(
					this,
					ShortcutKey.Notes,
					this.element.querySelectorAll(
						'[click\\.trigger="showNotesDialog()"]',
					),
				);
			}
			if (this.model.hasAuditButton) {
				KeyboardShortcut.subscribe(
					this,
					ShortcutKey.Audit,
					this.element.querySelectorAll(
						'[click\\.trigger="showAuditDialog()"]',
					),
				);
			}
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.Close,
				this.element.querySelectorAll('[click\\.trigger="close()"]'),
			);
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.Close,
				this.element.querySelectorAll(
					'[click\\.trigger="cancelEditOrClose()"]',
				),
			);
		}
	}

	public attached() {
		super.attached();

		this.subscription = this.bindingEngine
			.propertyObserver(this.classicPanel, 'formModel')
			.subscribe((formModel, oldFormModel) =>
				this.formModelChanged(formModel, oldFormModel),
			);

		// NOTE(Jae): 2020-03-02 - WEBUI-3021
		// Need to set `customisationConfig` here so it can immediately be used when processing formModel.
		// Binding in the templates doesn't take effect quick enough.
		// Also this saves us from having to edit every dialog template to feed this information into
		// a classicPanel
		if (
			this.model.customisationConfig !== null ||
			this.model.customisationConfig !== undefined
		) {
			// NOTE(Jae): 2020-03-02
			// This is enclosed in an if-statement because we don't want to override
			// the customisationConfig value setup for Receipt Maintenance Dialog, which
			// sets this value to "false" to mark it as disabled behaviour.
			this.classicPanel.customisationConfig =
				this.model.customisationConfig;
		}

		this.classicPanel.extraReadOnlyParameters =
			this.model.extraReadOnlyParameters;
		this.classicPanel.preSelectedParameters =
			this.model.preSelectedParameters;
		this.classicPanel.setQueryParams(this.model.queryParams);
		this.classicPanel.focusFirstField();

		if (this.preLoadedFormModel) {
			this.populateClassicPanelFormModel();
		}

		this.classicPanel.preSelectedParameters =
			this.model.preSelectedParameters;
		this.classicPanel.setQueryParams(this.model.queryParams);

		this.jQueryElement = $(this.element) as JQuery<HTMLElement>;
	}

	public detached() {
		super.detached();
		this.subscription.dispose();
	}

	// noinspection JSUnusedLocalSymbols
	protected deactivate(result: DialogCloseResult | DialogCloseError) {
		super.deactivate(result);
		// Note: York: WEBUI-2535
		// Only set dialog result output to this.classicPanel when it is empty
		if (!result.output) {
			result.output = this.classicPanel;
		}
	}

	@computedFrom('isSaving', 'classicPanel.isInEditMode')
	protected get canSave(): boolean {
		return !this.isSaving && this.classicPanel.isInEditMode;
	}

	protected checkIfCanSaveAndThrowError(): boolean {
		if (!this.canSave) {
			if (DEVELOPMENT) {
				throw new Error(
					'Development Mode Error: save() should not be called if "canSave" is false. Wrap calls to save() in a "canSave" if-statement.',
				);
			}
		}

		return this.canSave;
	}

	protected save() {
		if (!this.checkIfCanSaveAndThrowError()) {
			return;
		}

		this.isSaving = true;
		this.doSaveApi()
			.then((_) => {
				this.notifySavingSucceed();
			})
			.catch((error) => {
				this.notifyAndLogSavingError(
					this.getSaveFailureMessageKey(),
					error,
				);
			})
			.finally(() => {
				this.isSaving = false;
			});
	}

	protected notifySavingSucceed() {
		Notification.success(this.i18n.tr(this.getSaveSuccessMessageKey()));
		if (this.model.formType !== FormType.ADD) {
			this.classicPanel.setIsEditing(true);
		}
		this.controller.ok();
	}

	protected notifyAndLogSavingError(messageKey: string, error: any) {
		const message = FetchUtils.normalizeError(error);
		const errorMessage = this.i18n.tr(messageKey, { message });
		Logger.error(errorMessage);
		Notification.error(errorMessage);
	}

	protected doSaveApi(): Promise<
		FormModel<FormModelFieldNames> | InsertResult | undefined
	> {
		return this.classicPanel.save(
			this.model.customApi,
			this.model.customHttpMethod,
			this.model.formExtension,
		);
	}

	protected getSaveSuccessMessageKey(): string {
		return this.model.formType === FormType.ADD
			? 'successes.this-record-was-successfully-added'
			: 'successes.this-record-was-successfully-edited';
	}

	protected getSaveFailureMessageKey(): string {
		return this.model.formType === FormType.ADD
			? 'errors.unable-to-add-this-record'
			: 'errors.unable-to-edit-this-record';
	}

	protected close() {
		if (this.isSaving) {
			return;
		}
		this.classicPanel
			.promptToClosePanelIfChangedAndClearValidation()
			.then(() => this.controller.cancel());
	}

	protected readonly loadNotesDebounced = debounce(() => {
		if (DEVELOPMENT && !this.classicPanel.recordId) {
			throw new Error('Missing notes ID, got falsey/blank value.');
		}
		Logger.log('Getting notes for id:', this.classicPanel.recordId);
		this.notesApi
			.checkNotesData(
				this.model.functionName,
				this.classicPanel.recordId,
				this.model.queryParams,
			)
			.then((notesData) => {
				this.recordHasNotes = NotesApi.hasNotes(notesData);
			});
	}, DEBOUNCE_RECORD_SELECT_DURATION);

	public showNotesDialog(): Promise<DialogCloseResult> | Promise<undefined> {
		if (!this.classicPanel.recordId) {
			Logger.log('Must select a record before notes can shown');
			return Promise.resolve(undefined);
		}
		const notesDialogModel: NotesDialogModel = {
			noteTitle: this.i18n.tr('slideout.notes-title'),
			functionName: this.model.functionName,
			recordId: this.classicPanel.recordId,
			readOnly: false,
			queryParams: this.model.queryParams,
		};
		return this.dialogUtils
			.showCustomDialog(NotesDialog, notesDialogModel)
			.whenClosed(
				(
					dialogCloseResult: DialogCloseResult & {
						output: NotesDialogResult;
					},
				) => {
					const output: NotesDialogResult = dialogCloseResult.output;
					if (output.haveNotesChanged) {
						this.loadNotesDebounced();
					}
					return dialogCloseResult;
				},
			);
	}

	public showAuditDialog(): void {
		const auditDialogModel: AuditDialogModel = {
			functionName: this.model.functionName,
			recordId: this.classicPanel.recordId,
			auditApi: this.auditApi,
		};
		this.dialogUtils.showCustomDialog(AuditDialog, auditDialogModel);
	}

	public toggleDelete(): void {
		if (this.isSaving) {
			return;
		}
		const softDeleteFieldModel = FormModelUtils.getDeletedField(
			this.classicPanel.formModel,
		);

		const deleteYes: boolean = softDeleteFieldModel.value !== 'Y';
		const body = this.i18n.tr(DeleteHelper.confirmMessage(deleteYes));

		this.dialogUtils.showDialog({
			simpleDialogType: SimpleDialogType.WARNING,
			bodyText: body,
			okFunction: () => this.deleteRecord(deleteYes),
		});
	}

	protected deleteRecord(deleteYes: boolean): void {
		const resultMessages = DeleteHelper.resultMessages(deleteYes);

		// API controls which records can be hard deleted
		this.classicFormsApi
			.deleteRecord(
				this.classicPanel.recordId,
				this.model.functionName,
				deleteYes,
			)
			.then((model) => {
				this.classicPanel.processEditFormModel(model);
				FormModelUtils.showWarningMessagesIfPresent(model);
				Notification.success(this.i18n.tr(resultMessages.success));
				this.controller.ok();
			})
			.catch((reason) => {
				Notification.error(
					this.i18n.tr(resultMessages.error) + reason.message,
				);
			});
	}

	@computedFrom('classicPanel.formModel')
	get isRecordDeleted(): boolean {
		const fieldModel = FormModelUtils.getDeletedField(
			this.classicPanel.formModel,
		);
		if (fieldModel) {
			return fieldModel.value === 'Y';
		}
		return false;
	}

	@computedFrom('isRecordDeleted')
	get toggleDeleteButtonLabel(): string {
		if (!this.isRecordDeleted) {
			return this.i18n.tr('clickable-elements.delete');
		}
		return this.i18n.tr('clickable-elements.un-delete');
	}

	/**
	 * Can be used by subclasses which override the save method
	 */
	protected setIsSaving(isSaving: boolean) {
		this.isSaving = isSaving;
	}
}

export type ClassicPanelDialogConstructor<
	DialogModel extends ClassicPanelDialogModel,
> = new (...params: any[]) => ClassicPanelDialog<DialogModel>;
